import React, { useEffect, useMemo, useRef, useState } from 'react';

const storyContent = {
  ro: {
    label: 'Our Story',
    title: 'De la mutări mecanice la gândire de campion',
    subtitle:
      'ChessMindset.academy există pentru jucătorii care vor claritate strategică, nu doar memorare mecanică.',
    stats: [
      { value: 'Academy', text: 'Lecții ghidate, pas cu pas', area: 'academy' },
      { value: 'Daily Puzzle', text: 'Antrenament tactic zilnic', area: 'puzzles' },
      { value: 'Openings', text: 'Repertoriu și planuri de joc', area: 'openings' },
      { value: 'Mind Hub', text: 'Resurse pentru reziliență', area: 'mindhub' },
      { value: 'Community', text: 'Q&A, turnee și feedback', area: 'community' }
    ],
    ctaPrimary: 'Accesează Academia',
    steps: [
      {
        id: 'beginner',
        title: 'Începători',
        hook: 'De la confuzie la plan clar.',
        body:
          'Construim fundația corectă: principii, tactică de bază și obiceiuri de gândire care elimină mutările la întâmplare.'
      },
      {
        id: 'intermediate',
        title: 'Intermediari',
        hook: 'Din pattern-uri la decizii consistente.',
        body:
          'Antrenăm pattern recognition și calcul practic în poziții reale, cu accent pe decizii repetabile sub presiune.'
      },
      {
        id: 'performance',
        title: 'Performanță',
        hook: 'Când presiunea crește, gândirea rămâne limpede.',
        body:
          'Integrăm mental game, strategie profundă și pregătire competițională pentru jucători care vor performanță sustenabilă.'
      }
    ]
  },
  en: {
    label: 'Our Story',
    title: 'From mechanical moves to champion-level thinking',
    subtitle:
      'ChessMindset.academy is built for players who want strategic clarity, not mechanical memorization.',
    stats: [
      { value: 'Academy', text: 'Guided lessons, step by step', area: 'academy' },
      { value: 'Daily Puzzle', text: 'Daily tactical training', area: 'puzzles' },
      { value: 'Openings', text: 'Repertoire and game plans', area: 'openings' },
      { value: 'Mind Hub', text: 'Resources for resilience', area: 'mindhub' },
      { value: 'Community', text: 'Q&A, tournaments, feedback', area: 'community' }
    ],
    ctaPrimary: 'Enter Academy',
    steps: [
      {
        id: 'beginner',
        title: 'Beginner',
        hook: 'From confusion to a clear plan.',
        body:
          'We build the right foundation: principles, basic tactics, and thinking habits that replace random moves.'
      },
      {
        id: 'intermediate',
        title: 'Intermediate',
        hook: 'From patterns to consistent decisions.',
        body:
          'We train pattern recognition and practical calculation in real positions, focused on repeatable decisions under pressure.'
      },
      {
        id: 'performance',
        title: 'Performance',
        hook: 'When pressure rises, thinking stays clear.',
        body:
          'We integrate mental game, deep strategy, and competitive preparation for players pursuing sustainable performance.'
      }
    ]
  }
};

function OurStory({ language, onOpenAcademy }) {
  const content = storyContent[language] || storyContent.en;
  const stats = useMemo(() => content.stats || [], [content]);
  const steps = content.steps || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeStatArea, setActiveStatArea] = useState('');
  const [activeStatIndex, setActiveStatIndex] = useState(0);
  const [activeStatPage, setActiveStatPage] = useState(0);
  const [isMobileStats, setIsMobileStats] = useState(false);
  const timerRef = useRef(null);
  const navTimerRef = useRef(null);
  const statsTouchStartXRef = useRef(0);
  const mobileCardsPerPage = 3;

  const statsPages = useMemo(() => {
    const pages = [];
    stats.forEach((stat, index) => {
      const pageIndex = Math.floor(index / mobileCardsPerPage);
      if (!pages[pageIndex]) pages[pageIndex] = [];
      pages[pageIndex].push({ stat, index });
    });
    return pages;
  }, [stats]);

  // Keep active index stable across language changes (same step order).
  useEffect(() => {
    setActiveIndex((idx) => {
      const max = Math.max(0, (steps.length || 1) - 1);
      return Math.min(max, Math.max(0, idx));
    });
  }, [language, steps.length]);

  const selectedStep = steps[activeIndex] || steps[0];

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  const computeDelayMs = (step) => {
    const raw = `${step?.hook || ''} ${step?.body || ''}`.trim();
    const len = raw.length;
    // Enough time to read, but still feels like a gentle loop.
    return Math.max(4500, Math.min(9000, 4500 + len * 15));
  };

  const goTo = (nextIndex) => {
    const max = Math.max(0, (steps.length || 1) - 1);
    const clamped = Math.min(max, Math.max(0, nextIndex));
    setActiveIndex(clamped);
  };

  const openAreaFromStat = (area) => {
    const targetArea = area || 'academy';
    setActiveStatArea(targetArea);
    if (navTimerRef.current) window.clearTimeout(navTimerRef.current);
    // Small delay so the user can see the active (blue) click feedback.
    navTimerRef.current = window.setTimeout(() => {
      if (typeof onOpenAcademy === 'function') onOpenAcademy(targetArea);
    }, 220);
  };

  const goPrev = () => {
    const max = Math.max(0, (steps.length || 1) - 1);
    setActiveIndex((i) => (i <= 0 ? max : i - 1));
  };

  const goNext = () => {
    const max = Math.max(0, (steps.length || 1) - 1);
    setActiveIndex((i) => (i >= max ? 0 : i + 1));
  };

  // Auto-loop through steps.
  useEffect(() => {
    if (prefersReducedMotion) return undefined;
    if (!steps.length) return undefined;

    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      goNext();
    }, computeDelayMs(selectedStep));

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, language]);

  useEffect(() => {
    return () => {
      if (navTimerRef.current) window.clearTimeout(navTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 900px)');
    const apply = () => setIsMobileStats(Boolean(mq.matches));
    apply();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  useEffect(() => {
    setActiveStatIndex((prev) => {
      const max = Math.max(0, (stats.length || 1) - 1);
      return Math.min(prev, max);
    });
  }, [stats.length]);

  useEffect(() => {
    setActiveStatPage((prev) => {
      const max = Math.max(0, (statsPages.length || 1) - 1);
      return Math.min(prev, max);
    });
  }, [statsPages.length]);

  const goStatPageTo = (nextPage) => {
    if (!statsPages.length) return;
    const max = statsPages.length - 1;
    const clamped = Math.min(max, Math.max(0, nextPage));
    setActiveStatPage(clamped);
  };

  const goStatTo = (nextIndex) => {
    if (!stats.length) return;
    const max = stats.length - 1;
    const clamped = Math.min(max, Math.max(0, nextIndex));
    setActiveStatIndex(clamped);
    setActiveStatPage(Math.floor(clamped / mobileCardsPerPage));
  };

  const goStatPrev = () => goStatPageTo(activeStatPage - 1);
  const goStatNext = () => goStatPageTo(activeStatPage + 1);

  const handleStatsTouchStart = (e) => {
    statsTouchStartXRef.current = e.touches?.[0]?.clientX || 0;
  };

  const handleStatsTouchEnd = (e) => {
    const endX = e.changedTouches?.[0]?.clientX || 0;
    const deltaX = endX - statsTouchStartXRef.current;
    if (Math.abs(deltaX) < 36) return;
    if (deltaX < 0) goStatNext();
    else goStatPrev();
  };

  return (
    <section className="story-section">
      <div className="story-inner">
        <p className="story-label">{content.label}</p>
        <h2 className="story-title">{content.title}</h2>
        <p className="story-subtitle">{content.subtitle}</p>

        {isMobileStats && (
          <div className="story-stats-nav">
            <button
              type="button"
              className="story-arrow"
              onClick={goStatPrev}
              disabled={activeStatPage <= 0}
              aria-label={language === 'ro' ? 'Card anterior' : 'Previous card'}
            >
              ←
            </button>
            <span className="story-stats-index">{activeStatPage + 1}/{Math.max(1, statsPages.length)}</span>
            <button
              type="button"
              className="story-arrow"
              onClick={goStatNext}
              disabled={activeStatPage >= (statsPages.length - 1)}
              aria-label={language === 'ro' ? 'Card următor' : 'Next card'}
            >
              →
            </button>
          </div>
        )}

        {isMobileStats ? (
          <div
            className="story-stats story-stats-mobile"
            onTouchStart={handleStatsTouchStart}
            onTouchEnd={handleStatsTouchEnd}
          >
            {(statsPages[activeStatPage] || []).map(({ stat, index }) => (
              <button
                key={stat.text}
                className={`story-stat story-stat-btn ${(activeStatArea === (stat.area || 'academy') || activeStatIndex === index) ? 'active' : ''}`}
                type="button"
                onClick={() => {
                  goStatTo(index);
                  openAreaFromStat(stat.area);
                }}
                title={stat.value}
                aria-label={stat.value}
              >
                <strong>{stat.value}</strong>
                <span>{stat.text}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="story-stats">
            {stats.map((stat, idx) => (
              <button
                key={stat.text}
                className={`story-stat story-stat-btn ${(activeStatArea === (stat.area || 'academy') || activeStatIndex === idx) ? 'active' : ''}`}
                type="button"
                data-story-stat-index={idx}
                onClick={() => {
                  setActiveStatIndex(idx);
                  openAreaFromStat(stat.area);
                }}
                title={stat.value}
                aria-label={stat.value}
              >
                <strong>{stat.value}</strong>
                <span>{stat.text}</span>
              </button>
            ))}
          </div>
        )}

        <div className="story-journey">
          <div className="story-steps">
            {steps.map((step, idx) => (
              <button
                key={step.id}
                className={`story-step ${activeIndex === idx ? 'active' : ''}`}
                onClick={() => goTo(idx)}
              >
                {step.title}
              </button>
            ))}
          </div>

          <div className="story-panel">
            <div className="story-panel-head">
              <button
                type="button"
                className="story-arrow"
                onClick={() => {
                  goPrev();
                }}
                aria-label={language === 'ro' ? 'Înapoi' : 'Previous'}
              >
                ←
              </button>
              <div className="story-panel-index">
                {activeIndex + 1}/{steps.length}
              </div>
              <button
                type="button"
                className="story-arrow"
                onClick={() => {
                  goNext();
                }}
                aria-label={language === 'ro' ? 'Înainte' : 'Next'}
              >
                →
              </button>
            </div>

            <h3>{selectedStep.hook}</h3>
            <p>{selectedStep.body}</p>
          </div>
        </div>

        <div className="story-actions">
          <button className="btn-primary" onClick={onOpenAcademy}>
            {content.ctaPrimary}
          </button>
        </div>
      </div>
    </section>
  );
}

export default OurStory;
